import { useTranslation } from 'react-i18next'
import NavigationGroupsGroup from '@/components/common/menu/NavigationGroupsGroup.tsx'
import { NavigationDataT, NavItemsFooNamesT } from '@/lib/types/navigationTypes.ts'
import { removeNotAllowedGroups } from '@/lib/helpers/removeNotAllowedGroups.ts'
import { useUserStore } from '@/store/user.ts'
import { DesktopMenuLayerT, MobileMenuLayerT, useMenuStore } from '@/store/useMenuStore.ts'
import NavOrgsOverlay from '@/components/common/menu/navOverlays/navOrgsOverlay/NavOrgsOverlay.tsx'
import { useNavigate } from 'react-router-dom'
import { getLastAppPath } from '@/lib/helpers/visitedPaths.ts'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useIssuesStore } from '@/routes/projectStages/issues/lib/stores/useIssuesStore.ts'
import NavStagesPopover from '@/components/common/menu/navOverlays/navStagesOverlay/NavStagesPopover.tsx'
import NavUserData from '@/components/common/menu/NavUserData.tsx'
import AppVersion from '@/components/common/AppVersion.tsx'
import { LogOut } from 'lucide-react'

type PropsT = {
  navigationData: NavigationDataT
  menuLayer?: DesktopMenuLayerT | MobileMenuLayerT
}

export default function NavigationGroups({ navigationData, menuLayer }: PropsT) {
  const isSecondary = menuLayer === 'secondaryDesktop' || menuLayer === 'secondaryMobile'
  const isPrimaryMobile = menuLayer === 'primaryMobile'
  const { allowedNavigationGroups } = removeNotAllowedGroups(navigationData)
  const logoutUser = useUserStore(state => state.logoutUser)
  const resetStageStore = useProjectStageStore(state => state.resetStageStore)
  const openSecondaryMenu = useMenuStore(state => state.openSecondaryMenu)
  const { t } = useTranslation()
  const resetIssuesStore = useIssuesStore(state => state.resetIssuesStore)
  const closeSecondaryMenu = useMenuStore(state => state.closeSecondaryMenu)
  const closeMobileMenu = useMenuStore(state => state.closeMobileMenu)
  const currProjectStageIri = useProjectStageStore(state => state.iri)
  const navigate = useNavigate()

  function notImplemented() {
    console.log(`not implemented`)
    alert('Prace trwają 🚧')
  }

  function functionDecider(functionName: NavItemsFooNamesT) {
    switch (functionName) {
      case 'logout':
        resetStageStore()
        resetIssuesStore()
        logoutUser()
        navigate(`/login`)
        return
      case 'openSecondaryMenu':
        return openSecondaryMenu()
      case 'closeSecondaryMenu':
        return closeSecondaryMenu()
      case 'backToApp': {
        const fallback = currProjectStageIri ? `${currProjectStageIri}/dashboard` : '/'
        closeSecondaryMenu()
        closeMobileMenu()
        navigate(getLastAppPath(fallback))
        return
      }
      case 'notImplemented':
        return notImplemented()
    }
  }

  return (
    <div className={`relative h-full`}>
      {!isSecondary && (
        <>
          {/* Left-aligned logo with a thin full-width separator below. */}
          <div className={`hidden items-center border-b py-4 pl-4 md:flex`}>
            <img src="/logo.svg" alt={__BRAND_SHORT_NAME__} className={`h-[18px]`} />
          </div>
          {/* Stage switcher: desktop only. On mobile it lives in the topbar
              (NavigationMobileHeader) — the Więcej bottom sheet should be
              pure navigation, not context-switching. */}
          {!isPrimaryMobile && <NavStagesPopover />}
        </>
      )}
      {allowedNavigationGroups.map(key => {
        if (navigationData[key].pushToBottom) return

        return (
          <NavigationGroupsGroup
            key={key}
            navGroup={navigationData[key]}
            functionDecider={functionDecider}
            menuLayer={menuLayer}
          />
        )
      })}

      <div className={`sticky top-full`}>
        {allowedNavigationGroups.map(key => {
          if (!navigationData[key].pushToBottom) return

          return (
            <NavigationGroupsGroup
              key={key}
              navGroup={navigationData[key]}
              functionDecider={functionDecider}
              menuLayer={menuLayer}
            />
          )
        })}
        {!isSecondary ? (
          // Compact primary footer: identity + org picker. Left-aligned and
          // tight — previously this block was `items-center` with `p-4 gap-2`
          // which stacked wide centered rows and ate ~140px. Now rows sit
          // left-aligned with small vertical rhythm, the org picker is
          // full-width and directly actionable. The version string lives in
          // the Settings (secondary) footer instead — it's diagnostic info,
          // not something the user references day-to-day.
          //
          // The logout action is pulled out of the settings group and
          // rendered here as an icon button to the right of the user
          // identity, on both desktop and mobile. The nav list stays
          // pure-navigation and the "log out" gesture lives next to the
          // user it affects, which reads more naturally than a destructive
          // row sitting in the same vertical rhythm as regular destinations.
          <div
            className={`relative mt-auto flex w-full flex-col gap-1.5 border-t bg-prologisAccent px-3 py-2`}
          >
            <div className={`flex w-full items-center gap-2`}>
              <NavUserData />
              <button
                type="button"
                onClick={() => functionDecider('logout')}
                aria-label={t('common.logout')}
                data-cy="nav-logout-button"
                className={`ml-auto flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground`}
              >
                <LogOut className={`size-4`} />
              </button>
            </div>
            <NavOrgsOverlay />
          </div>
        ) : (
          // Secondary (Settings) footer: just the app version. Kept here so
          // it's discoverable for support/debugging without crowding the
          // primary drawer that field users see all day.
          <div
            className={`relative mt-auto flex w-full flex-col border-t bg-prologisAccent px-3 py-2`}
          >
            <AppVersion />
          </div>
        )}
      </div>
    </div>
  )
}
